import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, Sparkles, Clock, ShieldCheck, Zap } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1544984243-ec57ea16fe25?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const INTERIOR = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const VILLA = "https://images.unsplash.com/photo-1599777560450-e462cffc5368?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fade = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };

export default function Home() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState({ products: [], addons: [] });

  useEffect(() => { api.get("/catalog").then(({ data }) => setCatalog(data)); }, []);

  const listing = catalog.products.filter((p) => p.category === "listing");
  const vision = catalog.products.filter((p) => p.category === "vision");

  return (
    <div className="bg-[#090A0F] min-h-screen">
      <Header />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-20 grain overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO} alt="Luxury estate" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090A0F] via-[#090A0F]/85 to-[#090A0F]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] via-transparent to-[#090A0F]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.div {...fade} className="max-w-3xl">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-6">
              Autonomous Property Cinema
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.05] tracking-tight">
              Property Video.<br /><span className="gold-text">Without the video shoot.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-300 max-w-xl leading-relaxed">
              Turn the photos, listings and renderings you already have into cinematic property films—fast.
              Quality-checked and delivered to your inbox in under 24 hours.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/order/most-popular")}
                className="bg-[#D4AF37] text-black hover:bg-[#E2C044] rounded-full h-14 px-8 text-base gold-glow" data-testid="create-listing-video-button">
                Create a Listing Video <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button onClick={() => navigate("/order/vision-standard")} variant="outline"
                className="border-[#D4AF37]/40 text-white hover:bg-[#D4AF37]/10 rounded-full h-14 px-8 text-base" data-testid="create-vision-film-button">
                Create a Vision Film
              </Button>
            </div>
            <div className="mt-12 flex flex-wrap gap-8 text-sm text-slate-400">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4AF37]" /> Under 24h delivery</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Autonomous QA</span>
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#D4AF37]" /> No shoot required</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TWO ROUTES */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-6" data-testid="routes-section">
        <motion.div {...fade} className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/15 group">
          <img src={INTERIOR} alt="Listing" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] via-[#090A0F]/80 to-[#090A0F]/40" />
          <div className="relative p-10 flex flex-col h-full min-h-[380px]">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Have a listing?</p>
            <h2 className="font-display text-4xl text-white mb-3">Listing Cinematics</h2>
            <p className="text-slate-300 mb-6 max-w-md">Paste your property link or upload your photos. Get a social-ready or full listing film.</p>
            <div className="mt-auto space-y-2.5">
              {listing.map((p) => (
                <button key={p.id} onClick={() => navigate(`/order/${p.id}`)}
                  className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-[#181B26]/80 border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 hover:bg-[#202433] transition-all group/item"
                  data-testid={`route-product-${p.id}`}>
                  <span className="text-white font-medium">{p.name.replace(" Listing Cinematic", "")}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[#D4AF37]">${p.price}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover/item:text-[#D4AF37] group-hover/item:translate-x-1 transition-all" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div {...fade} className="relative rounded-2xl overflow-hidden border border-[#D4AF37]/15 group">
          <img src={VILLA} alt="Vision" className="absolute inset-0 w-full h-full object-cover opacity-25 group-hover:opacity-35 group-hover:scale-105 transition-all duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090A0F] via-[#090A0F]/80 to-[#090A0F]/40" />
          <div className="relative p-10 flex flex-col h-full min-h-[380px]">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Property, hotel or development?</p>
            <h2 className="font-display text-4xl text-white mb-3">Vision Films</h2>
            <p className="text-slate-300 mb-6 max-w-md">Upload renderings, plans and photography. Premium cinematic storytelling for high-value properties.</p>
            <div className="mt-auto space-y-2.5">
              {vision.map((p) => (
                <button key={p.id} onClick={() => navigate(`/order/${p.id}`)}
                  className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-[#181B26]/80 border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 hover:bg-[#202433] transition-all group/item"
                  data-testid={`route-product-${p.id}`}>
                  <span className="text-white font-medium">{p.name}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-mono text-[#D4AF37]">${p.price.toLocaleString()}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover/item:text-[#D4AF37] group-hover/item:translate-x-1 transition-all" />
                  </span>
                </button>
              ))}
              <button onClick={() => navigate("/custom")}
                className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/20 transition-all group/item"
                data-testid="route-custom-vision">
                <span className="text-[#D4AF37] font-medium flex items-center gap-2"><Sparkles className="w-4 h-4" /> Need Something Custom?</span>
                <ArrowRight className="w-4 h-4 text-[#D4AF37] group-hover/item:translate-x-1 transition-all" />
              </button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* PRODUCT GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="products-section">
        <motion.div {...fade} className="text-center mb-14">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Productized Cinema</p>
          <h2 className="font-display text-4xl sm:text-5xl text-white">Choose your production tier</h2>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {catalog.products.map((p) => (
            <motion.div key={p.id} {...fade}
              className={`relative rounded-2xl p-7 flex flex-col bg-gradient-to-b from-[#181B26] to-[#12141D] border transition-all duration-300
                ${p.badge === "Most Popular" ? "border-[#D4AF37]/50 gold-glow" : "border-[#D4AF37]/15 hover:border-[#D4AF37]/40"}`}
              data-testid={`product-card-${p.id}`}>
              {p.badge === "Most Popular" && (
                <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-xs font-semibold">Most Popular</span>
              )}
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]/80 mb-3">{p.badge}</p>
              <h3 className="font-display text-2xl text-white leading-tight mb-2">{p.name}</h3>
              <p className="text-sm text-slate-400 mb-4 flex-1">{p.description}</p>
              <p className="text-3xl font-mono text-white mb-1">${p.price.toLocaleString()}</p>
              <p className="text-xs text-slate-500 mb-5">{p.deliverable}</p>
              <Button onClick={() => navigate(`/order/${p.id}`)}
                className={`rounded-full ${p.badge === "Most Popular" ? "bg-[#D4AF37] text-black hover:bg-[#E2C044]" : "bg-white/5 text-white border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}
                data-testid={`select-product-${p.id}`}>
                Select
              </Button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="workflow-section">
        <motion.div {...fade} className="glass rounded-3xl p-10 lg:p-16">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">The autonomous pipeline</p>
          <h2 className="font-display text-4xl text-white mb-10">Pay. Submit assets. Receive a finished film.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ["Select & Pay", "Pick a tier and check out — or use an approved test code."],
              ["Submit Assets", "Paste a listing link or upload photos, renderings and plans."],
              ["Autonomous Production", "AI generation, QA, repair and final assembly run automatically."],
              ["Email Delivery", "Quality-checked films land in your inbox in under 24 hours."],
            ].map(([t, d], i) => (
              <div key={t} className="relative">
                <span className="font-display text-5xl text-[#D4AF37]/30">0{i + 1}</span>
                <h4 className="text-white font-medium mt-2 mb-1">{t}</h4>
                <p className="text-sm text-slate-400">{d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
